import { useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useOpsEvalsData } from './OpsEvalsContext'
import { ACTIVE_PROMPT_SET, DRAFT_PROMPT_SET } from './mockData'
import type { ItemResult, ModelResult, PipelineStage, PromptSet, Run, RunConfig } from './types'

export type EvalsView = 'list' | 'trends'

/** Candidate/baseline defaults for the new-run wizard, with optional prefill from the
 *  Ops → Prompts "Evaluate draft" hook (`?stage=&version=`). */
export type NewRunPrefill = {
  datasetId?: string
  candidate: PromptSet
  baseline: PromptSet
}

export type FocusedItem = { modelResult: ModelResult; item: ItemResult }

export type UseEvalsPanelReturn = {
  runs: Run[]
  seeding: boolean
  running: boolean
  view: EvalsView
  selectedRun: Run | null
  isNewRunOpen: boolean
  prefill: NewRunPrefill
  focusedItem: FocusedItem | null
  openRun: (id: string) => void
  closeRun: () => void
  showList: () => void
  showTrends: () => void
  openNewRun: () => void
  closeNewRun: () => void
  selectItem: (model: string, itemId: string) => void
  clearItem: () => void
  runAndOpen: (config: RunConfig) => Promise<void>
}

export function useEvalsPanel(): UseEvalsPanelReturn {
  const { runs, seeding, running, createRun } = useOpsEvalsData()
  const [searchParams, setSearchParams] = useSearchParams()

  const runId = searchParams.get(`run`)
  const view = (searchParams.get(`view`) as EvalsView) === `trends` ? `trends` : `list`
  const isNewRunOpen = searchParams.get(`new`) === `1`
  const itemId = searchParams.get(`item`)
  const modelParam = searchParams.get(`model`)

  const selectedRun = useMemo(() => runs.find((run) => run.id === runId) ?? null, [runs, runId])

  const prefill = useMemo<NewRunPrefill>(() => {
    // `stage=single` names the single-prompt pipeline, which the candidate then runs.
    const stage = searchParams.get(`stage`) as PipelineStage | `single` | null
    const version = Number(searchParams.get(`version`))
    const candidate: PromptSet = stage && version ? { ...ACTIVE_PROMPT_SET, [stage]: version } : { ...DRAFT_PROMPT_SET }
    return { datasetId: searchParams.get(`dataset`) ?? undefined, candidate, baseline: { ...ACTIVE_PROMPT_SET } }
  }, [searchParams])

  const focusedItem = useMemo<FocusedItem | null>(() => {
    if (!selectedRun || !itemId) return null
    const modelResult =
      selectedRun.modelResults.find((result) => result.model === modelParam) ?? selectedRun.modelResults[0]
    const item = modelResult?.items.find((entry) => entry.itemId === itemId)
    return modelResult && item ? { modelResult, item } : null
  }, [selectedRun, itemId, modelParam])

  const patch = (mutate: (next: URLSearchParams) => void) => {
    const next = new URLSearchParams(searchParams)
    mutate(next)
    setSearchParams(next)
  }

  return {
    runs,
    seeding,
    running,
    view,
    selectedRun,
    isNewRunOpen,
    prefill,
    focusedItem,
    openRun: (id) => setSearchParams({ run: id }),
    closeRun: () => setSearchParams({}),
    showList: () => setSearchParams({}),
    showTrends: () => setSearchParams({ view: `trends` }),
    openNewRun: () => patch((next) => next.set(`new`, `1`)),
    closeNewRun: () =>
      patch((next) => {
        next.delete(`new`)
        next.delete(`stage`)
        next.delete(`version`)
      }),
    selectItem: (model, id) =>
      patch((next) => {
        next.set(`item`, id)
        next.set(`model`, model)
      }),
    clearItem: () =>
      patch((next) => {
        next.delete(`item`)
        next.delete(`model`)
      }),
    runAndOpen: async (config) => {
      const id = await createRun(config)
      if (id) setSearchParams({ run: id })
    },
  }
}
